'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiToggleButton, {
  type ToggleButtonTypeMap as MuiToggleButtonTypeMap,
} from '@mui/material/ToggleButton';

type ExcludedMuiProps = 'disableFocusRipple' | 'disableRipple';

type CustomProps = {
  component?: ElementType;
};

type ToggleButtonTypeMap<
  P = CustomProps,
  D extends ElementType = MuiToggleButtonTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiToggleButtonTypeMap['props'], ExcludedMuiProps> & P;
};

export type ToggleButtonProps<
  D extends ElementType = MuiToggleButtonTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiToggleButtonTypeMap<P, D>, D>;

const ToggleButtonComponent = (
  props: ToggleButtonProps,
  ref: Ref<HTMLButtonElement>
) => {
  return <MuiToggleButton {...props} ref={ref} />;
};

ToggleButtonComponent.displayName = 'ToggleButton';

export const ToggleButton = forwardRef(
  ToggleButtonComponent
) as OverridableComponent<ToggleButtonTypeMap>;
